import { Component, define } from '@theme/component';

/**
 * The bar with the name, the price and "Order now" that slides up from the
 * bottom of the product page once its own buy button has scrolled out of
 * view, so the button is never more than a tap away on a page that goes on
 * to tell the whole story of the machine.
 *
 * The bar has no form of its own: its button presses the real one, so the
 * variant chosen in the dropdowns, the cart drawer and any error message all
 * stay the buy column's. For the same reason it copies that button's
 * disabled state each time it appears.
 *
 * It steps aside again once the footer comes into view, so it never covers
 * the footer's links.
 *
 * The homepage has the same bar (sections/sticky-order.liquid), with a link
 * to the product page for its button. There `data-watch` names what it waits
 * to pass — the hero — instead of a button to stand in for.
 */
class StickyBuy extends Component {
  static requiredRefs = ['button'];

  /** @type {IntersectionObserver[]} */
  #observers = [];

  /** Past the button (or the hero): the bar is wanted. */
  #passed = false;

  /** The footer is on screen: the bar steps aside so it covers nothing. */
  #atFooter = false;

  connected() {
    const target = this.#target;
    const watched = target ?? document.querySelector(this.dataset.watch ?? '');
    if (!watched) return;

    if (target) this.on(this.refs.button, 'click', () => target.click());

    // Only once the button has gone off the top: above the buy column, on
    // the way in, the bar would cover the very thing it stands in for.
    this.#observe(watched, (entry) => {
      this.#passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      if (target) this.refs.button.toggleAttribute('disabled', target.hasAttribute('disabled'));
    });

    const footer = document.querySelector('.footer');
    if (footer) {
      this.#observe(footer, (entry) => {
        this.#atFooter = entry.isIntersecting;
      });
    }
  }

  disconnected() {
    for (const observer of this.#observers) observer.disconnect();
    this.#observers = [];
  }

  /**
   * @param {Element} element
   * @param {(entry: IntersectionObserverEntry) => void} update
   */
  #observe(element, update) {
    const observer = new IntersectionObserver(([entry]) => {
      update(entry);
      const visible = this.#passed && !this.#atFooter;
      this.toggleAttribute('data-visible', visible);
      this.toggleAttribute('inert', !visible);
    });
    observer.observe(element);
    this.#observers.push(observer);
  }

  /** @returns {HTMLButtonElement | null} */
  get #target() {
    return document.getElementById(this.dataset.target ?? '');
  }
}

define('sticky-buy', StickyBuy);
